import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  McButton,
  McIcon,
  McLoadingIndicator,
  McTag
} from '@maersk-global/mds-react-wrapper/components-core';
import { McInput, McSelect } from '@maersk-global/mds-react-wrapper';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { useInfiniteQuery } from '@tanstack/react-query';
import { queryService } from '../../services/queryService';
import { FavoriteButton } from './FavoriteButton';
import { useApplicationAccess } from '../../hooks/useApplicationAccess';

interface QueryListSidebarProps {
  isOpen: boolean;
  onSelectQuery: (queryId: string) => void;
  selectedQueryId?: string | null;
  onCreateNew?: () => void;
  externalApplicationId?: string;
  onApplicationChange?: (id: string) => void;
}

export const QueryListSidebar: React.FC<QueryListSidebarProps> = ({
  isOpen,
  onSelectQuery,
  selectedQueryId,
  onCreateNew,
  externalApplicationId,
  onApplicationChange,
}) => {
  const [internalApplicationId, setInternalApplicationId] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { accessibleApplications } = useApplicationAccess();
  const navigate = useNavigate();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const effectiveAppId = externalApplicationId !== undefined ? externalApplicationId : internalApplicationId;
  const effectiveSetAppId = externalApplicationId !== undefined ? (onApplicationChange ?? (() => {})) : setInternalApplicationId;

  // Debounce search input (500 ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(textFilter.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [textFilter]);

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    error,
  } = useInfiniteQuery({
    queryKey: ['queries', 'active', 'All', debouncedSearch, undefined, undefined, [], effectiveAppId],
    queryFn: ({ pageParam = 0 }) =>
      queryService.searchQueries({
        applicationId: effectiveAppId || undefined,
        activeOnly: true,
        searchTerm: debouncedSearch || undefined,
        page: pageParam as number,
        size: 20,
        sortBy: 'updatedAt',
        sortDirection: 'DESC',
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage.number ?? lastPage.page ?? 0;
      const nextPage = currentPage + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    staleTime: 30000,
  });

  // Trigger next page load when the sentinel div scrolls into view
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const isLoading = isPending;
  const queries: any[] = infiniteData?.pages.flatMap((page: any) => page.content ?? []) ?? [];
  const totalElements: number = infiniteData?.pages[0]?.totalElements ?? 0;
  const hasApplicationFilter = Boolean(effectiveAppId);

  const handleQueryClick = (queryId: string) => {
    onSelectQuery(queryId);
  };

  return (
    <>
      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          left: 'var(--app-main-drawer-width, 64px)',
          top: '64px',
          width: 'clamp(220px, 24vw, 300px)',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e5e7eb',
          boxShadow: isOpen ? '2px 0 8px rgba(0,0,0,0.08)' : 'none',
          zIndex: 999,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'transform 0.3s ease, visibility 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '10px 12px 8px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>Saved Queries {totalElements > 0 ? <span style={{ fontWeight: 400, color: '#6b7280' }}>({totalElements})</span> : ''}</h3>
            {onCreateNew && (
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }}
                         onMouseOut={(e: any) => { e.currentTarget.open = false; }}
                         position="bottom-center">
                <McButton
                  variant="filled"
                  appearance="primary"
                  fit="small"
                  onClick={onCreateNew}
                  style={{ minWidth: '32px', padding: '0 8px' }}
                >
                  <McIcon icon="plus" />
                </McButton>
                <span>Create new query</span>
              </McTooltip>
            )}
          </div>

          {accessibleApplications.length > 1 && (
            <div style={{ marginBottom: '6px' }}>
              <McSelect
                label="Application"
                hiddenlabel
                placeholder="All applications"
                value={effectiveAppId}
                data={accessibleApplications.map((app) => ({
                  label: app.name || app.applicationId,
                  value: app.applicationId,
                }))}
                optionselected={(e: any) => effectiveSetAppId(e.detail?.value ?? '')}
                style={{ width: '100%', fontSize: '12px' }}
              />
            </div>
          )}

          <div style={{ marginBottom: '6px' }}>
            <McInput
              placeholder="Filter by tag, name, or category"
              value={textFilter}
              label="Filter queries"
              hiddenlabel
              input={(e: any) => setTextFilter(e.target.value)}
              style={{ width: '100%' }}
            >
              <McIcon icon="magnifying-glass" slot="prefix" />
            </McInput>
          </div>

        </div>

        {/* Query List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <McLoadingIndicator />
            </div>
          ) : error ? (
            <div style={{ padding: '12px', textAlign: 'center', color: '#dc2626', fontSize: '12px' }}>
              <div>Failed to load queries</div>
            </div>
          ) : queries.length > 0 ? (
            queries.map((query: any) => {
              const selectableQueryId = query.queryId || (query.id ? String(query.id) : '');
              const isSelected = selectedQueryId === selectableQueryId;

              return (
              <div
                key={selectableQueryId || `${query.name || 'query'}-${query.updatedAt || Math.random()}`}
                onClick={() => selectableQueryId && handleQueryClick(selectableQueryId)}
                style={{
                  padding: '7px 8px',
                  marginBottom: '2px',
                  borderRadius: '4px',
                  cursor: selectableQueryId ? 'pointer' : 'not-allowed',
                  backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                  border: isSelected ? '1px solid #93c5fd' : '1px solid transparent',
                  transition: 'background 0.12s',
                  opacity: selectableQueryId ? 1 : 0.6,
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#ffffff'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <FavoriteButton queryId={query.queryId} isFavorite={query.isFavorite} size="sm" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '1px', wordBreak: 'break-word' }}>
                      {selectableQueryId || '—'}
                    </div>
                    {query.name && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px', wordBreak: 'break-word', lineHeight: 1.3 }}>
                        {query.name}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'wrap' }}>
                      <McTag
                        label={(query.active ?? query.isActive) ? 'Active' : 'Inactive'}
                        appearance={(query.active ?? query.isActive) ? 'success' : 'neutral'}
                        fit="small"
                      />
                      {query.category && (
                        <McTag label={query.category} appearance="info" fit="small" />
                      )}
                    </div>
                  </div>
                  {selectableQueryId && (
                    <McTooltip position="right-center">
                      <div
                        slot="trigger"
                        onClick={(e) => { e.stopPropagation(); navigate(`/queries/${selectableQueryId}`); }}
                        style={{ display: 'flex', alignItems: 'center', padding: '2px', borderRadius: '4px', cursor: 'pointer', color: '#6b7280', flexShrink: 0 }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <McIcon icon="square-arrow-up-right" size="16" />
                      </div>
                      <span slot="content">Open query detail</span>
                    </McTooltip>
                  )}
                </div>
              </div>
            );
            })
          ) : (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
              <div>
                {hasApplicationFilter
                  ? 'No queries found for selected application.'
                  : "You don't have any queries you can access."}
              </div>
            </div>
          )}

          {/* Sentinel for infinite scroll */}
          <div ref={loadMoreRef} style={{ height: '1px' }} />

          {isFetchingNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
              <McLoadingIndicator />
            </div>
          )}
        </div>
      </div>
    </>
  );
};